import QRCode from 'qrcode';
import { type FormEvent, useEffect, useState } from 'react';
import { Alert, Button, Card, Field } from '../components/ui';
import { t } from '../i18n';
import { api } from '../lib/api';
import { useAuth, useMe } from '../lib/auth';
import { errorMessage } from '../lib/errors';
import { useStepUp } from '../lib/step-up';
import { ChangePasswordForm } from './PasswordPages';

type Step = { kind: 'idle' } | { kind: 'setup'; otpauthUrl: string; secret: string } | { kind: 'codes'; codes: string[] } | { kind: 'disable' };

/** Verificación en dos pasos: activar (QR + código), códigos de recuperación y desactivar. */
export function SecurityPage() {
  const me = useMe();
  const { reloadMe } = useAuth();
  const { run, prompt } = useStepUp();
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      if (!(err instanceof Error && err.message === 'cancelado')) setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const startSetup = () =>
    act(async () => {
      const res = await api<{ otpauthUrl: string; secret: string }>('/api/auth/2fa/setup', { method: 'POST' });
      setStep({ kind: 'setup', ...res });
    });

  const regenerate = () =>
    act(async () => {
      const res = await run(() => api<{ recoveryCodes: string[] }>('/api/auth/2fa/recovery-codes', { method: 'POST' }));
      setStep({ kind: 'codes', codes: res.recoveryCodes });
    });

  return (
    <div className="max-w-2xl space-y-4">
      <h1 className="text-2xl font-semibold text-tinta">{t('seguridad.titulo')}</h1>
      {prompt}
      {error && <Alert>{error}</Alert>}

      <Card title={t('clave.cambiar_titulo')}>
        <ChangePasswordForm />
      </Card>

      <Card title={t('seguridad.2fa')}>
        {step.kind === 'idle' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{me.twoFactorEnabled ? t('seguridad.2fa_activa') : t('seguridad.2fa_inactiva')}</p>
            {me.twoFactorEnabled ? (
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" disabled={busy} onClick={() => void regenerate()}>
                  {t('seguridad.regenerar')}
                </Button>
                <Button variant="danger" disabled={busy} onClick={() => setStep({ kind: 'disable' })}>
                  {t('seguridad.desactivar')}
                </Button>
              </div>
            ) : (
              <Button disabled={busy} onClick={() => void startSetup()}>
                {t('seguridad.activar')}
              </Button>
            )}
          </div>
        )}

        {step.kind === 'setup' && (
          <SetupStep
            otpauthUrl={step.otpauthUrl}
            secret={step.secret}
            onEnabled={(codes) => setStep({ kind: 'codes', codes })}
            onCancel={() => setStep({ kind: 'idle' })}
          />
        )}

        {step.kind === 'codes' && (
          <RecoveryCodes
            codes={step.codes}
            onDone={() => {
              setStep({ kind: 'idle' });
              void reloadMe();
            }}
          />
        )}

        {step.kind === 'disable' && (
          <CodeForm
            text={t('seguridad.desactivar_confirmar')}
            submitLabel={t('seguridad.desactivar')}
            danger
            onSubmit={async (code) => {
              await api('/api/auth/2fa/disable', { json: { code } });
              setStep({ kind: 'idle' });
              await reloadMe();
            }}
            onCancel={() => setStep({ kind: 'idle' })}
          />
        )}
      </Card>
    </div>
  );
}

function SetupStep({
  otpauthUrl,
  secret,
  onEnabled,
  onCancel,
}: {
  otpauthUrl: string;
  secret: string;
  onEnabled: (codes: string[]) => void;
  onCancel: () => void;
}) {
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    // El QR se genera en el navegador: el secreto no se envía a ningún servicio externo.
    void QRCode.toDataURL(otpauthUrl, { margin: 1, width: 200 }).then(setQr);
  }, [otpauthUrl]);

  return (
    <div className="space-y-4">
      <p className="text-sm">{t('seguridad.paso_qr')}</p>
      {qr && <img src={qr} alt="Código QR para la aplicación autenticadora" className="h-48 w-48 rounded border border-slate-200" />}
      <p className="text-sm text-slate-600">
        {t('seguridad.clave_manual')} <code className="break-all rounded bg-slate-100 px-1 py-0.5 text-xs">{secret}</code>
      </p>
      <CodeForm
        text={t('seguridad.paso_codigo')}
        submitLabel={t('seguridad.activar')}
        onSubmit={async (code) => {
          const res = await api<{ recoveryCodes: string[] }>('/api/auth/2fa/enable', { json: { code } });
          onEnabled(res.recoveryCodes);
        }}
        onCancel={onCancel}
      />
    </div>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');
  const download = () => {
    const url = URL.createObjectURL(new Blob([`SIT 365 · Códigos de recuperación\n\n${text}\n`], { type: 'text/plain' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: 'sit365-codigos-recuperacion.txt' });
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-3">
      <Alert tone="warning">
        <strong>{t('seguridad.codigos_titulo')}.</strong> {t('seguridad.codigos_aviso')}
      </Alert>
      <ul className="grid grid-cols-2 gap-1 rounded bg-slate-50 p-3 font-mono text-sm">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={download}>
          {t('seguridad.codigos_descargar')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(text).then(() => setCopied(true));
          }}
        >
          {copied ? t('seguridad.codigos_copiados') : t('seguridad.codigos_copiar')}
        </Button>
        <Button onClick={onDone}>{t('seguridad.codigos_listo')}</Button>
      </div>
    </div>
  );
}

function CodeForm({
  text,
  submitLabel,
  danger,
  onSubmit,
  onCancel,
}: {
  text: string;
  submitLabel: string;
  danger?: boolean;
  onSubmit: (code: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(code.trim());
    } catch (err) {
      setError(errorMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm">{text}</p>
      {error && <Alert>{error}</Alert>}
      <Field id="codigo-2fa" label={t('mfa.codigo')} autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
      <div className="flex gap-2">
        <Button type="submit" variant={danger ? 'danger' : 'primary'} disabled={busy || code.trim().length < 6}>
          {submitLabel}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('comun.cancelar')}
        </Button>
      </div>
    </form>
  );
}
